import { action, makeObservable, observable } from 'mobx'

import Project from '~/client/src/shared/models/Project'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import { LOGOUT } from '~/client/src/shared/stores/EventStore/eventConstants'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { UPDATE_PROJECT_IS_LOGO_WHITE } from '../../../shared/stores/EventStore/eventConstants'
import desktopRoutes from '../../constants/desktopRoutes'
import DesktopInitialState from '../../stores/DesktopInitialState'
import DesktopEventStore from '../../stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '../../stores/ui/DesktopCommon.store'
import PresentationModeStore from '../../stores/ui/PresentationMode.store'

// localization: translated

export default class NavBarStore {
  @observable public shouldShowSettingsMenu: boolean = false
  @observable public isPresentationStopped: boolean = false
  @observable public recalculating: boolean = true
  @observable public visibleCount: number = 100

  public constructor(
    private readonly common: DesktopCommonStore,
    private readonly eventsStore: DesktopEventStore,
    private readonly projectsStore: ProjectsStore,
    private readonly state: DesktopInitialState,
    private readonly presentationModeStore: PresentationModeStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly notificationsStore: NotificationsStore,
  ) {
    makeObservable(this)
  }

  @action.bound
  public selectProject(project: Project) {
    this.projectsStore.selectProject(project.id)
  }

  @action.bound
  public clickOnSettings() {
    if (!this.state.isPresentationMode) {
      this.shouldShowSettingsMenu = !this.shouldShowSettingsMenu
      return
    }
    this.showStopPresentationDialog()
  }

  @action.bound
  public clickOnProjectSetup() {
    this.displayView(desktopRoutes.PROJECT_SETUP)
    this.clickOnSettings()
  }

  @action.bound
  public clickOnUserSettings() {
    this.displayView(desktopRoutes.USER_PROFILE)
    this.clickOnSettings()
  }

  @action.bound
  public clickOnNotifications() {
    if (!this.state.isPresentationMode) {
      this.displayView(desktopRoutes.NOTIFICATIONS)
    }
  }

  public clickOnNotificationPreferences = () => {
    this.common._displayView(
      `${desktopRoutes.PROJECT_SETUP}?page=notification-settings`,
    )
  }

  @action.bound
  public logoutHandler() {
    this.hideStopPresentationDialog()
    this.presentationModeStore.cancelPresentation()
    this.state.hideChat()
    this.displayView(desktopRoutes.LOGIN)
    this.eventsStore.dispatch(LOGOUT)
  }

  @action.bound
  public showStopPresentationDialog() {
    this.isPresentationStopped = true
  }

  @action.bound
  public hideStopPresentationDialog() {
    this.isPresentationStopped = false
  }

  @action.bound
  public setVisibleCount(count: number): void {
    this.visibleCount = count
  }

  @action.bound
  public setRecalculating(value: boolean): void {
    this.recalculating = value
  }

  @action.bound
  public displayView(route: string) {
    this.common._displayView(route)
  }

  public get isProjectSelectorDisabled() {
    return this.hasNoProjects || this.state.isPresentationMode
  }

  public get isDeliveriesDisabled() {
    return this.state.isDeliveriesDisabled
  }

  public get isFormsDisabled() {
    return this.state.isWorkflowsDisabled
  }

  public get isMaterialsDisabled() {
    return this.state.isMaterialsDisabled
  }

  public get isMaterialResourcesDisabled() {
    return this.state.isMaterialResourcesDisabled
  }

  public get isEquipmentDisabled() {
    return this.state.isEquipmentDisabled
  }

  public get isLocationsDisabled() {
    return this.state.isLocationsDisabled
  }

  public get isStruxTagsDisabled() {
    return this.state.isStruxTagsDisabled
  }

  public get isPhotosDisabled() {
    return this.state.isPhotosDisabled
  }

  public get isAnalyticsDisabled() {
    return this.state.isAnalyticsDisabled
  }

  public get isLogisticsDisabled() {
    return this.state.isLogisticsDisabled
  }

  public get isProjectSelectorDisplayed() {
    return this.common.isProjectSelectorDisplayed
  }

  public get projects() {
    return this.projectsStore.list
  }

  public get activeProject() {
    return this.projectsStore.activeProject
  }

  public get hasNoProjects() {
    return this.projects && this.projects.length === 0
  }

  public get shouldShowProjectClock() {
    return this.projectDateStore.isProjectTimezoneDifferent
  }

  public get user() {
    return this.state.user
  }

  public get location() {
    return this.common.history.location
  }

  public get unreadNotificationsCount() {
    return this.notificationsStore.unreadNotificationsCount
  }

  public saveIsLogoWhite(isLogoWhite: boolean) {
    // Intentional loose check
    if (this.activeProject.isLogoWhite == null) {
      this.eventsStore.dispatch(
        UPDATE_PROJECT_IS_LOGO_WHITE,
        isLogoWhite,
        (error: any) => {
          console.error('Failed to update project isLogoWhite:', error)
        },
      )
    }
  }
}
